<div class="">
  <div class="max-content-width spaced-sections">
    <!-- PI-only notice -->
    @if (piOnlyNotice) {
      <div
        class="flex gap-3 rounded-r-lg border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-800 shadow-sm"
        data-testid="pi-only-notice"
      >
        <div>
          <p class="font-bold">Access Restricted</p>
          <p class="mt-0.5">{{ piOnlyNotice }}</p>
          <a
            class="mt-2 inline-block text-xs font-semibold text-amber-700 underline hover:text-amber-900"
            href="/projects"
            >Back to Projects</a
          >
        </div>
      </div>
    }

    <!-- Error / Success Alerts -->
    @if (errorMessage) {
      <app-base-alert
        [type]="'error'"
        [message]="errorMessage"
        [closeable]="true"
        (closed)="errorMessage = ''"
      ></app-base-alert>
    }
    @if (successMessage) {
      <app-base-alert
        [type]="'success'"
        [message]="successMessage"
        [closeable]="true"
        (closed)="successMessage = ''"
      ></app-base-alert>
    }

    <!-- Back to screening -->
    <div class="flex items-center gap-3 justify-between">
      <h2 class="section-title">Conflict Resolution</h2>
      <button
        class="btn-secondary text-sm inline-flex items-center gap-1.5"
        (click)="backToScreening()"
      >
        <i class="bi bi-arrow-left"></i> Back to Full-Text Screening
      </button>
    </div>

    <!-- Stats bar -->
    <div class="grid-2-col">
      <div class="card card-padding">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Unresolved Conflicts
        </h3>
        <span class="text-2xl font-bold text-red-600">{{ unresolvedCount }}</span>
        <p class="text-xs text-slate-400 mt-1">Papers awaiting PI decision</p>
      </div>
      <div class="card card-padding">
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
          Resolved This Session
        </h3>
        <span class="text-2xl font-bold text-green-600">{{ resolvedCount }}</span>
        <p class="text-xs text-slate-400 mt-1">Conflicts resolved so far</p>
      </div>
    </div>

    <!-- Loading -->
    @if (loading) {
      <div class="card card-padding flex flex-col items-center justify-center min-h-[200px]">
        <div
          class="w-10 h-10 border-4 border-secondary border-t-transparent rounded-full animate-spin"
        ></div>
        <p class="text-sm text-slate-500 mt-4">Loading conflicts...</p>
      </div>
    }

    <!-- No conflicts -->
    @if (!loading && conflicts.length === 0) {
      <div class="card card-padding flex items-center justify-center min-h-[200px]">
        <div class="empty-state">
          <div class="empty-state-icon">✅</div>
          <h3 class="empty-state-title">No Conflicts</h3>
          <p class="empty-state-message">
            There are no unresolved conflicts in this project's full-text screening phase.
          </p>
          <div class="mt-6">
            <button class="btn-primary" (click)="backToScreening()">
              Back to Full-Text Screening
            </button>
          </div>
        </div>
      </div>
    }

    <!-- Conflicts Table -->
    @if (!loading && conflicts.length > 0) {
      <div class="card p-0 overflow-hidden">
        <!-- Table Header -->
        <div
          class="hidden md:grid grid-cols-[1fr_160px_160px_160px] gap-4 px-5 py-3 bg-slate-50 border-b border-slate-200/60 text-xs font-semibold uppercase tracking-wide text-slate-500"
        >
          <div>Paper</div>
          <div>Reviewer A</div>
          <div>Reviewer B</div>
          <div>Status</div>
        </div>

        <!-- Rows -->
        @for (conflict of conflicts; track conflict.paperToProjectId) {
          <div
            class="border-b border-slate-200/60 last:border-b-0 transition-colors"
            [class.bg-green-50]="conflict.resolved"
            [class.bg-white]="!conflict.resolved && !conflict.expanded"
            [class.bg-slate-50]="!conflict.resolved && conflict.expanded"
          >
            <!-- Summary Row -->
            <div
              class="grid grid-cols-1 md:grid-cols-[1fr_160px_160px_160px] gap-3 md:gap-4 items-center px-5 py-4 cursor-pointer hover:bg-slate-50/80 transition-colors"
              (click)="toggleRow(conflict)"
            >
              <!-- Title & Authors -->
              <div class="min-w-0">
                <p class="font-semibold text-sm text-slate-900 truncate">{{ conflict.title }}</p>
                <p class="text-xs text-slate-400 truncate mt-0.5">{{ conflict.authors }}</p>
                @if (conflict.doi) {
                  <p class="text-xs text-slate-400 font-mono mt-0.5">DOI: {{ conflict.doi }}</p>
                }
              </div>

              <!-- Decision 1 -->
              <div class="flex items-center gap-1.5 text-xs">
                @if (conflict.decision1.includeDecision) {
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-semibold"
                  >
                    <i class="bi bi-check-circle-fill text-green-500"></i> Include
                  </span>
                } @else {
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-semibold"
                  >
                    <i class="bi bi-x-circle-fill text-red-500"></i> Exclude
                  </span>
                }
                <span class="text-slate-400 text-[10px]">{{
                  conflict.decision1.screenerName
                }}</span>
              </div>

              <!-- Decision 2 -->
              <div class="flex items-center gap-1.5 text-xs">
                @if (conflict.decision2.includeDecision) {
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 font-semibold"
                  >
                    <i class="bi bi-check-circle-fill text-green-500"></i> Include
                  </span>
                } @else {
                  <span
                    class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-semibold"
                  >
                    <i class="bi bi-x-circle-fill text-red-500"></i> Exclude
                  </span>
                }
                <span class="text-slate-400 text-[10px]">{{
                  conflict.decision2.screenerName
                }}</span>
              </div>

              <!-- Status + PDF button -->
              <div class="flex items-center gap-2 flex-wrap" (click)="$event.stopPropagation()">
                @if (conflict.resolved) {
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 border border-green-200"
                  >
                    <i class="bi bi-check2-circle"></i> Resolved
                  </span>
                } @else if (conflict.expanded) {
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-secondary/10 text-secondary border border-secondary/20"
                  >
                    <i class="bi bi-pencil-fill"></i> Deciding
                  </span>
                } @else {
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 cursor-pointer"
                    (click)="toggleRow(conflict)"
                  >
                    <i class="bi bi-exclamation-triangle-fill"></i> Pending
                  </span>
                }

                <!-- View PDF button — only show if paper has a PDF -->
                @if (conflict.pdfId || conflict.pdfUrl) {
                  <button
                    type="button"
                    (click)="openPdfModal(conflict)"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 hover:text-slate-800 transition-colors cursor-pointer"
                    title="View PDF"
                  >
                    <i class="bi bi-file-earmark-pdf-fill text-red-500"></i> PDF
                  </button>
                }

                @if (!conflict.resolved) {
                  <i
                    class="bi text-slate-400 text-xs cursor-pointer"
                    [class.bi-chevron-down]="!conflict.expanded"
                    [class.bi-chevron-up]="conflict.expanded"
                    (click)="toggleRow(conflict)"
                  ></i>
                }
              </div>
            </div>

            <!-- Expanded Panel -->
            @if (conflict.expanded && !conflict.resolved) {
              <div class="px-5 pb-5 border-t border-slate-200/60 pt-4 space-y-5">
                <!-- Decision Context -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <!-- Decision 1 -->
                  <div class="info-box space-y-1">
                    <h4 class="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                      <i class="bi bi-person-fill mr-1"></i>{{ conflict.decision1.screenerName }}'s
                      Decision
                    </h4>
                    @if (conflict.decision1.includeDecision) {
                      <p class="text-sm font-semibold text-green-700">
                        <i class="bi bi-check-circle-fill mr-1"></i>Include
                      </p>
                    } @else {
                      <p class="text-sm font-semibold text-red-700">
                        <i class="bi bi-x-circle-fill mr-1"></i>Exclude
                      </p>
                      @if (conflict.decision1.exclusionReason) {
                        <p class="text-xs text-slate-500 mt-1">
                          Reason:
                          <span class="font-medium text-slate-700">{{
                            conflict.decision1.exclusionReason
                          }}</span>
                        </p>
                      }
                    }
                  </div>
                  <!-- Decision 2 -->
                  <div class="info-box space-y-1">
                    <h4 class="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                      <i class="bi bi-person-fill mr-1"></i>{{ conflict.decision2.screenerName }}'s
                      Decision
                    </h4>
                    @if (conflict.decision2.includeDecision) {
                      <p class="text-sm font-semibold text-green-700">
                        <i class="bi bi-check-circle-fill mr-1"></i>Include
                      </p>
                    } @else {
                      <p class="text-sm font-semibold text-red-700">
                        <i class="bi bi-x-circle-fill mr-1"></i>Exclude
                      </p>
                      @if (conflict.decision2.exclusionReason) {
                        <p class="text-xs text-slate-500 mt-1">
                          Reason:
                          <span class="font-medium text-slate-700">{{
                            conflict.decision2.exclusionReason
                          }}</span>
                        </p>
                      }
                    }
                  </div>
                </div>

                <!-- Resolution Options -->
                <div>
                  <h4 class="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                    Your Resolution
                  </h4>
                  <div class="flex flex-col gap-2">
                    <!-- Accept Decision 1 -->
                    <button
                      type="button"
                      (click)="selectDecision(conflict, 'accept1')"
                      [class.ring-2]="conflict.selectedDecision === 'accept1'"
                      [class.ring-secondary]="conflict.selectedDecision === 'accept1'"
                      class="w-full flex items-center justify-between p-3.5 rounded-xl border border-slate-200 bg-white hover:border-secondary/40 hover:bg-secondary/5 transition-all text-left shadow-sm cursor-pointer"
                    >
                      <div class="flex items-center gap-3">
                        <span class="text-lg text-secondary flex items-center"
                          ><i class="bi bi-person-check-fill"></i
                        ></span>
                        <div class="flex flex-col">
                          <span class="font-bold text-slate-900 text-sm"
                            >Accept {{ conflict.decision1.screenerName }}'s Decision</span
                          >
                          <span class="text-xs text-slate-500">
                            {{
                              conflict.decision1.includeDecision
                                ? 'Include the paper'
                                : 'Exclude — ' +
                                  (conflict.decision1.exclusionReason || 'no reason given')
                            }}
                          </span>
                        </div>
                      </div>
                      @if (conflict.selectedDecision === 'accept1') {
                        <i class="bi bi-check-circle-fill text-secondary"></i>
                      }
                    </button>

                    <!-- Accept Decision 2 -->
                    <button
                      type="button"
                      (click)="selectDecision(conflict, 'accept2')"
                      [class.ring-2]="conflict.selectedDecision === 'accept2'"
                      [class.ring-secondary]="conflict.selectedDecision === 'accept2'"
                      class="w-full flex items-center justify-between p-3.5 rounded-xl border border-slate-200 bg-white hover:border-secondary/40 hover:bg-secondary/5 transition-all text-left shadow-sm cursor-pointer"
                    >
                      <div class="flex items-center gap-3">
                        <span class="text-lg text-secondary flex items-center"
                          ><i class="bi bi-person-check-fill"></i
                        ></span>
                        <div class="flex flex-col">
                          <span class="font-bold text-slate-900 text-sm"
                            >Accept {{ conflict.decision2.screenerName }}'s Decision</span
                          >
                          <span class="text-xs text-slate-500">
                            {{
                              conflict.decision2.includeDecision
                                ? 'Include the paper'
                                : 'Exclude — ' +
                                  (conflict.decision2.exclusionReason || 'no reason given')
                            }}
                          </span>
                        </div>
                      </div>
                      @if (conflict.selectedDecision === 'accept2') {
                        <i class="bi bi-check-circle-fill text-secondary"></i>
                      }
                    </button>

                    <!-- Custom Decision -->
                    <button
                      type="button"
                      (click)="selectDecision(conflict, 'custom')"
                      [class.ring-2]="conflict.selectedDecision === 'custom'"
                      [class.ring-secondary]="conflict.selectedDecision === 'custom'"
                      class="w-full flex items-center justify-between p-3.5 rounded-xl border border-slate-200 bg-white hover:border-secondary/40 hover:bg-secondary/5 transition-all text-left shadow-sm cursor-pointer"
                    >
                      <div class="flex items-center gap-3">
                        <span class="text-lg text-amber-500 flex items-center"
                          ><i class="bi bi-pencil-square"></i
                        ></span>
                        <div class="flex flex-col">
                          <span class="font-bold text-slate-900 text-sm">Set Custom Decision</span>
                          <span class="text-xs text-slate-500"
                            >Manually choose include or exclude</span
                          >
                        </div>
                      </div>
                      @if (conflict.selectedDecision === 'custom') {
                        <i class="bi bi-check-circle-fill text-secondary"></i>
                      }
                    </button>
                  </div>
                </div>

                <!-- Custom decision form -->
                @if (conflict.selectedDecision === 'custom') {
                  <div class="border border-slate-200 rounded-xl p-4 bg-slate-50/50 space-y-4">
                    <h5 class="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Custom Decision Details
                    </h5>
                    <div class="flex gap-3">
                      <button
                        type="button"
                        (click)="conflict.customInclude = true; conflict.customExclusionReason = ''"
                        [class.ring-2]="conflict.customInclude === true"
                        [class.ring-green-500]="conflict.customInclude === true"
                        class="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border border-slate-200 bg-white hover:border-green-300 hover:bg-green-50/50 transition-all text-sm font-semibold text-slate-700 cursor-pointer"
                      >
                        <i class="bi bi-check-circle-fill text-green-500"></i> Include
                      </button>
                      <button
                        type="button"
                        (click)="conflict.customInclude = false"
                        [class.ring-2]="conflict.customInclude === false"
                        [class.ring-red-500]="conflict.customInclude === false"
                        class="flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border border-slate-200 bg-white hover:border-red-300 hover:bg-red-50/50 transition-all text-sm font-semibold text-slate-700 cursor-pointer"
                      >
                        <i class="bi bi-x-circle-fill text-red-500"></i> Exclude
                      </button>
                    </div>

                    @if (conflict.customInclude === false) {
                      <div>
                        <label class="text-xs font-semibold text-slate-600 mb-1.5 block"
                          >Exclusion Reason <span class="text-red-500">*</span></label
                        >
                        @if (exclusionReasons.length > 0) {
                          <select
                            [(ngModel)]="conflict.customExclusionReason"
                            class="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
                          >
                            <option value="">-- Select a reason --</option>
                            @for (reason of exclusionReasons; track reason) {
                              <option [value]="reason">{{ reason }}</option>
                            }
                          </select>
                        } @else {
                          <input
                            type="text"
                            [(ngModel)]="conflict.customExclusionReason"
                            placeholder="Enter exclusion reason..."
                            class="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
                          />
                        }
                      </div>
                    }
                  </div>
                }

                <!-- Submit row: View PDF + action buttons together -->
                <div class="flex items-center justify-between gap-3 pt-1 flex-wrap">
                  <!-- View PDF shortcut from expanded panel -->
                  @if (conflict.pdfId || conflict.pdfUrl) {
                    <button
                      type="button"
                      (click)="openPdfModal(conflict)"
                      class="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50 hover:border-slate-300 transition-colors cursor-pointer shadow-sm"
                    >
                      <i class="bi bi-file-earmark-pdf-fill text-red-500"></i> View PDF
                    </button>
                  } @else {
                    <span class="text-xs text-slate-400 italic"
                      >No PDF available for this paper</span
                    >
                  }

                  <div class="flex items-center gap-3 ml-auto">
                    <button
                      type="button"
                      (click)="conflict.expanded = false; conflict.selectedDecision = null"
                      class="btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      (click)="submitResolution(conflict)"
                      [disabled]="!canSubmit(conflict) || conflict.resolving"
                      class="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      @if (conflict.resolving) {
                        <span class="flex items-center gap-2">
                          <span
                            class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block"
                          ></span>
                          Resolving...
                        </span>
                      } @else {
                        Confirm Resolution
                      }
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }

    <!-- All resolved CTA -->
    @if (!loading && conflicts.length > 0 && unresolvedCount === 0) {
      <div class="card card-padding text-center">
        <div class="text-3xl mb-3">🎉</div>
        <h3 class="font-bold text-slate-900 mb-1">All Conflicts Resolved!</h3>
        <p class="text-sm text-slate-500">
          All screening conflicts have been resolved. The project can now advance to the next stage.
        </p>
        <div class="mt-4">
          <button class="btn-primary" (click)="backToScreening()">
            Back to Full-Text Screening
          </button>
        </div>
      </div>
    }
  </div>
</div>

<!-- ═══ PDF Modal Overlay ══════════════════════════════════════════════════ -->
@if (pdfModalConflict) {
  <!-- Backdrop -->
  <div
    class="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
    (click)="closePdfModal()"
  >
    <!-- Modal panel — stop click from bubbling to backdrop -->
    <div
      class="relative bg-white rounded-2xl shadow-2xl flex flex-col w-full max-w-5xl overflow-hidden"
      style="height: 90vh"
      (click)="$event.stopPropagation()"
    >
      <!-- Modal header -->
      <div
        class="flex items-start justify-between px-5 py-4 border-b border-slate-200/60 flex-shrink-0"
      >
        <div class="min-w-0 pr-4">
          <h2 class="font-bold text-slate-900 text-base truncate">{{ pdfModalConflict.title }}</h2>
          <p class="text-xs text-slate-400 truncate mt-0.5">{{ pdfModalConflict.authors }}</p>
        </div>
        <button
          type="button"
          (click)="closePdfModal()"
          class="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 transition-colors cursor-pointer text-base font-bold leading-none"
          title="Close"
        >
          &times;
        </button>
      </div>

      <!-- Modal body -->
      <div class="flex-1 overflow-hidden relative">
        <!-- Loading state -->
        @if (pdfLoading) {
          <div class="absolute inset-0 flex flex-col items-center justify-center bg-white">
            <div
              class="w-10 h-10 border-4 border-secondary border-t-transparent rounded-full animate-spin"
            ></div>
            <p class="text-sm text-slate-500 mt-4">Loading PDF...</p>
          </div>
        }

        <!-- Error state -->
        @if (!pdfLoading && pdfError) {
          <div
            class="absolute inset-0 flex flex-col items-center justify-center bg-white text-center p-8"
          >
            <i class="bi bi-file-earmark-x text-4xl text-red-400 mb-3"></i>
            <p class="text-sm font-semibold text-slate-700">{{ pdfError }}</p>
            <p class="text-xs text-slate-400 mt-1">The PDF could not be loaded for this paper.</p>
          </div>
        }

        <!-- PDF viewer -->
        @if (!pdfLoading && !pdfError) {
          <app-cad-pdf-viewer
            [showZoomBar]="true"
            class="w-full h-full -mt-1 -mb-1"
          ></app-cad-pdf-viewer>
        }
      </div>
    </div>
  </div>
}
